import { userRequest } from "./requestMethods";

/**
 * Helper to unwrap standard backend response { success, message, data }
 */
const unwrap = (res) => {
  const data = res?.data;
  if (
    data &&
    typeof data === "object" &&
    "data" in data &&
    (data.success !== undefined || data.message !== undefined)
  ) {
    return data.data;
  }
  return data;
};

/**
 * Normalizes a class_group_id field to a plain string id, whether the backend
 * returned it as a raw ObjectId/string or as a populated object (e.g. { _id, name }).
 * Returns null when there is no group id at all, so callers can compare safely
 * with `getClassGroupId(a) === getClassGroupId(b)` without risking a
 * "[object Object]" string match.
 */
export const getClassGroupId = (classGroupIdField) => {
  if (!classGroupIdField) return null;
  if (typeof classGroupIdField === "object") {
    return classGroupIdField._id ? String(classGroupIdField._id) : null;
  }
  return String(classGroupIdField);
};

/**
 * Resolves the primary teacher ID for a subject, checking `subject_teachers[]`
 * for an active primary assignment first, and falling back to the legacy
 * `subject_teacher` field if `subject_teachers[]` is empty/absent.
 * Handles both raw string/ObjectId and populated `{ _id, ... }` teacher objects.
 */
export const getPrimarySubjectTeacher = (subject) => {
  if (!subject) return null;
  const activePrimary = (subject.subject_teachers || []).find(
    (a) => a.role === "primary" && a.status === "active",
  );
  if (activePrimary) {
    const t = activePrimary.teacher;
    if (!t) return null;
    return typeof t === "object" ? (t._id ? String(t._id) : null) : String(t);
  }
  const legacy = subject.subject_teacher;
  if (!legacy) return null;
  return typeof legacy === "object" ? (legacy._id ? String(legacy._id) : null) : String(legacy);
};

// 1. Create a class group
export const createClassGroup = async (schoolId, name) => {
  const res = await userRequest.post(`/class-groups/${schoolId}`, { name });
  return unwrap(res);
};

// 2. List all class groups for a school
export const listClassGroups = async (schoolId) => {
  const res = await userRequest.get(`/class-groups/${schoolId}`);
  return unwrap(res);
};

// 3. List subclasses belonging to a class group
export const listClassGroupMembers = async (schoolId, classGroupId) => {
  const res = await userRequest.get(`/class-groups/${schoolId}/${classGroupId}/members`);
  return unwrap(res);
};

// Lists the shared subject documents for a class group — the actual source of truth for a
// subject's class-level teacher/assistants, unlike any one member subclass's embedded copy
// (which can legitimately diverge per arm once overrides/assistants are involved).
export const listClassGroupSubjects = async (schoolId, classGroupId) => {
  const res = await userRequest.get(`/class-groups/${schoolId}/${classGroupId}/subjects`);
  return unwrap(res);
};

// 4. Link subclasses to a class group
export const linkSubclassesToClassGroup = async (schoolId, classGroupId, subclasses) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/subclasses`, {
    subclasses,
  });
  return unwrap(res);
};

// 5. Unlink a subclass from its class group
export const unlinkSubclassFromClassGroup = async (schoolId, classId) => {
  const res = await userRequest.delete(`/class-groups/${schoolId}/subclasses/${classId}`);
  return unwrap(res);
};

// 6. Create a shared subject and apply to member subclasses
export const createSharedSubject = async (schoolId, classGroupId, subjectName) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/subjects`, {
    subject_name: subjectName,
  });
  return unwrap(res);
};

// 7. Link existing embedded subjects to a shared subject
export const linkSubjectToSharedSubject = async (
  schoolId,
  classGroupId,
  subjectId,
  embeddedSubjectIds,
) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/subjects/link`, {
    subject_id: subjectId,
    embedded_subject_ids: embeddedSubjectIds,
  });
  return unwrap(res);
};

// 8. Assign a teacher to a shared subject. role is "primary" (default — replaces the
// existing class-level default) or "assistant" (additive — multiple may accumulate;
// assigning the same teacher twice while active is rejected by the backend).
export const assignClassLevelTeacher = async (
  schoolId,
  classGroupId,
  subjectId,
  teacherId,
  role = "primary",
) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/subjects/teacher`, {
    subject_id: subjectId,
    teacher_id: teacherId,
    role,
  });
  return unwrap(res);
};

/**
 * Resolves the primary teacher ID for a subclass arm, checking `teachers[]`
 * for an active primary assignment first, and falling back to the legacy
 * `teacher` field if `teachers[]` is empty/absent.
 */
export const getPrimaryClassTeacher = (cls) => {
  if (!cls) return null;
  const activePrimary = (cls.teachers || []).find(
    (a) => a.role === "primary" && a.status === "active",
  );
  if (activePrimary) {
    const t = activePrimary.teacher;
    if (!t) return null;
    return typeof t === "object" ? (t._id ? String(t._id) : null) : String(t);
  }
  const legacy = cls.teacher;
  if (!legacy) return null;
  return typeof legacy === "object" ? (legacy._id ? String(legacy._id) : null) : String(legacy);
};

/**
 * Returns the active ASSISTANT entries from a subclass's teachers[], normalized to
 * plain teacher ids (handles both a raw ObjectId/string and a populated {_id, ...} teacher).
 */
export const getActiveAssistantClassTeachers = (cls) => {
  if (!cls) return [];
  return (cls.teachers || [])
    .filter((a) => a.role === "assistant" && a.status === "active")
    .map((a) => a.teacher?._id || a.teacher)
    .filter(Boolean);
};

/**
 * Returns the active ASSISTANT entries from a subject's subject_teachers[], normalized to
 * plain teacher ids (handles both a raw ObjectId/string and a populated {_id, ...} teacher).
 */
export const getActiveAssistantTeachers = (subject) => {
  if (!subject) return [];
  return (subject.subject_teachers || [])
    .filter((a) => a.role === "assistant" && a.status === "active")
    .map((a) => a.teacher?._id || a.teacher)
    .filter(Boolean);
};

// 9. Set class-level default tuition
export const setClassLevelDefaultTuition = async (schoolId, classGroupId, classPrice) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/fees/tuition`, {
    class_price: classPrice,
  });
  return unwrap(res);
};

// 10. Set class-level default add-ons
export const setClassLevelDefaultAddOns = async (schoolId, classGroupId, addOns) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/fees/add-on`, {
    add_on: addOns,
  });
  return unwrap(res);
};

// 11. List fee state for a class group's members
export const listClassGroupFeeState = async (schoolId, classGroupId) => {
  const res = await userRequest.get(`/class-groups/${schoolId}/${classGroupId}/fees`);
  return unwrap(res);
};

// 12. Delete a class group — unlinks every member subclass and deletes all its shared subjects.
// -> { unlinked_subclasses: string[], deleted_shared_subjects: number }
export const deleteClassGroup = async (schoolId, classGroupId) => {
  const res = await userRequest.delete(`/class-groups/${schoolId}/${classGroupId}`);
  return unwrap(res);
};

// 13. Delete a shared subject — clears subject_ref_id on every linked embedded subject.
// -> { deleted_subject, unlinked_embedded_subject_ids: string[] }
export const deleteSharedSubject = async (schoolId, classGroupId, subjectId) => {
  const res = await userRequest.delete(
    `/class-groups/${schoolId}/${classGroupId}/subjects/${subjectId}`,
  );
  return unwrap(res);
};

// 14. Create one test per member subclass of a class group in a single call. Can partially
// succeed — the response is a 200-level success even when some subclasses fail, so callers
// must branch on `res.failed.length`, not rely on a catch block for partial failures.
// payload: { subject_id, title, description, duration, total_score, is_active, term, session,
//            type, ca?, category, scoring_mode?, mcq_max_score?, theory_max_score?,
//            start_date?, end_date? }
// -> { shared_subject, created: [{ class_id, test }], failed: [{ class_id, error }] }
export const createSharedTest = async (schoolId, classGroupId, payload) => {
  const res = await userRequest.post(`/class-groups/${schoolId}/${classGroupId}/tests`, payload);
  return unwrap(res);
};

/**
 * Format helper for error extraction from API responses
 */
export const extractApiErrorMessage = (error, fallback = "An unexpected error occurred") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.response?.data?.error) return error.response.data.error;
  if (typeof error.response?.data === "string") return error.response.data;
  if (error.message) return error.message;
  return fallback;
};
